import { useEffect } from 'react';

const Disclaimers = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  return (
    <>
      <title>Disclaimers - Veridian Vanguard Advisors</title>
      <meta name="robots" content="noindex, nofollow" />
      <link rel="canonical" href={`${siteUrl}/disclaimers`} />

      <div className="bg-slate-900 text-white min-h-screen font-sans">
        {/* Navigation */}
        <nav className="bg-slate-800/95 backdrop-blur-sm shadow-lg">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center space-x-4">
                <span className="text-2xl font-light tracking-wide">VERIDIAN VANGUARD</span>
              </a>
              <a 
                href="/"
                className="bg-lime-400 text-slate-800 px-6 py-2 rounded-lg font-medium hover:bg-lime-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Home
              </a>
            </div>
          </div>
        </nav>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="space-y-12">
            <div>
              <h1 className="text-5xl font-extralight mb-4">
                LEGAL <span className="text-lime-400">DISCLAIMERS</span>
              </h1>
              <p className="text-white/60 text-sm">Last Updated: January 27, 2025</p>
            </div>

            <div className="space-y-8 text-white/80 leading-relaxed">
              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">Professional Advisory Disclaimer</h2>
                <div className="bg-lime-400/10 border-l-4 border-lime-400 p-6 rounded-r-lg">
                  <p className="font-medium text-white">
                    The information provided on this website and through our advisory services is for general informational 
                    purposes only and does not constitute professional advice.
                  </p>
                </div>
                <p>
                  Veridian Vanguard Advisors provides strategic advisory services based on our professional expertise and 
                  market knowledge. However, all advice and recommendations should be considered in the context of your 
                  specific circumstances, and we strongly recommend consulting with appropriate legal, financial, tax, and 
                  other professional advisors before making any business decisions.
                </p>
                <p>
                  Our advisory services do not constitute legal advice, investment advice, tax advice, or any other form of 
                  regulated professional service unless explicitly stated in a formal engagement agreement.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">No Guarantees or Warranties</h2>
                <p>
                  While we strive to provide accurate, current, and reliable information and advice, we make no representations 
                  or warranties of any kind, express or implied, about:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>The completeness, accuracy, reliability, or suitability of any information or advice provided</li>
                  <li>The results that may be achieved through implementation of our recommendations</li>
                  <li>The performance of any investments, partnerships, or business ventures we advise on</li>
                  <li>Market conditions, regulatory changes, or other external factors affecting outcomes</li>
                </ul>
                <p className="mt-4">
                  Past performance and historical results are not indicative of future outcomes. All business ventures and 
                  strategic initiatives involve inherent risks.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">Limitation of Liability</h2>
                <p>
                  To the fullest extent permitted by applicable law, Veridian Vanguard Advisors, its principals, employees, 
                  and affiliates shall not be liable for any direct, indirect, incidental, consequential, special, or punitive 
                  damages arising from:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Use of or reliance on information provided on this website</li>
                  <li>Implementation of advisory recommendations</li>
                  <li>Business decisions made based on our strategic guidance</li>
                  <li>Any errors, omissions, or inaccuracies in information provided</li>
                  <li>Interruption or cessation of advisory services</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">Anti-Money Laundering Compliance</h2>
                <div className="bg-white/5 border border-lime-400/20 p-6 rounded-lg">
                  <p className="font-medium text-white mb-3">AML/KYC Commitment</p>
                  <p>
                    Veridian Vanguard Advisors is committed to preventing money laundering and terrorist financing. 
                    We maintain robust compliance procedures in accordance with applicable anti-money laundering (AML) 
                    and know-your-customer (KYC) regulations.
                  </p>
                </div>
                <p className="mt-4">
                  As part of our onboarding process, we conduct thorough due diligence on all prospective clients, including:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Identity verification and background checks</li>
                  <li>Source of funds and wealth verification</li>
                  <li>Screening against sanctions lists and politically exposed persons (PEP) databases</li>
                  <li>Ongoing monitoring of client relationships and transactions</li>
                  <li>Reporting of suspicious activities to relevant authorities as required by law</li>
                </ul>
                <p className="mt-4">
                  We reserve the right to decline or terminate any client relationship if we are unable to complete 
                  satisfactory due diligence or if we identify potential compliance concerns.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">Conflict of Interest Disclosure</h2>
                <p>
                  Veridian Vanguard Advisors is committed to maintaining the highest standards of professional integrity 
                  and transparency. We recognize that conflicts of interest may arise in the course of our advisory work.
                </p>
                <h3 className="text-xl font-medium text-lime-400 mt-6">Our Conflict Management Approach:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Disclosure:</strong> We proactively disclose any actual or potential conflicts of interest to affected clients</li>
                  <li><strong>Independence:</strong> We maintain independence in our advisory recommendations and do not accept compensation that could compromise our objectivity</li>
                  <li><strong>Client Priority:</strong> When conflicts arise, we prioritize our clients' interests and seek their informed consent before proceeding</li>
                  <li><strong>Separation:</strong> We implement information barriers and other safeguards when advising clients with competing interests</li>
                  <li><strong>Declining Engagements:</strong> We may decline engagements where conflicts cannot be adequately managed</li>
                </ul>
                <p className="mt-4">
                  Clients should be aware that we may provide advisory services to multiple parties within the same industry 
                  or market sector. We maintain strict confidentiality protocols to protect proprietary information.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">Regulatory Compliance Notices</h2>
                <h3 className="text-xl font-medium text-lime-400 mt-6">Multi-Jurisdictional Operations</h3>
                <p>
                  Veridian Vanguard Advisors operates across multiple jurisdictions and complies with applicable laws and 
                  regulations in each market where we provide services. Our operations are subject to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>United States:</strong> Compliance with federal and state business advisory regulations</li>
                  <li><strong>European Union:</strong> GDPR data protection requirements and applicable business regulations</li>
                  <li><strong>United Kingdom:</strong> Post-Brexit regulatory framework for advisory services</li>
                  <li><strong>Middle East:</strong> Local business licensing and advisory service regulations</li>
                  <li><strong>Other Jurisdictions:</strong> Compliance with local laws where we engage with clients</li>
                </ul>

                <h3 className="text-xl font-medium text-lime-400 mt-6">Investment Advisory Disclaimer</h3>
                <p>
                  Veridian Vanguard Advisors is not a registered investment advisor, broker-dealer, or financial institution. 
                  Our investment-related advisory services are limited to strategic guidance and do not include:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Direct management of client investment portfolios</li>
                  <li>Execution of securities transactions</li>
                  <li>Custody of client funds or assets</li>
                  <li>Provision of regulated investment advice as defined by securities laws</li>
                </ul>
                <p className="mt-4">
                  Clients seeking investment management services should engage appropriately licensed and regulated 
                  investment professionals.
                </p>

                <h3 className="text-xl font-medium text-lime-400 mt-6">Sports and Entertainment Advisory</h3>
                <p>
                  Our advisory services to athletes and entertainment professionals are strategic in nature and do not 
                  constitute agent representation, contract negotiation services, or other regulated activities unless 
                  we hold appropriate licenses in relevant jurisdictions.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">Confidentiality and Non-Disclosure</h2>
                <p>
                  We maintain strict confidentiality regarding all client information and engagements. However, clients 
                  should be aware that:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>We may be required to disclose information pursuant to legal process or regulatory requirements</li>
                  <li>Confidentiality obligations may be limited by applicable law</li>
                  <li>We may use anonymized case studies and aggregated data for marketing purposes</li>
                  <li>Formal non-disclosure agreements govern specific engagement terms</li>
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">Third-Party Links and References</h2>
                <p>
                  Our website may contain links to third-party websites or references to external resources. We do not 
                  endorse, control, or assume responsibility for the content, privacy policies, or practices of any 
                  third-party sites or services.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">Forward-Looking Statements</h2>
                <p>
                  Any statements regarding future market conditions, business opportunities, or potential outcomes are 
                  forward-looking in nature and subject to significant uncertainties. Actual results may differ materially 
                  from projections or expectations due to various factors beyond our control.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">Modification of Services and Terms</h2>
                <p>
                  We reserve the right to modify, suspend, or discontinue any aspect of our services or website at any 
                  time without prior notice. We may also update these disclaimers periodically to reflect changes in our 
                  practices or legal requirements.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">Governing Law and Dispute Resolution</h2>
                <p>
                  These disclaimers and any disputes arising from our advisory services shall be governed by the laws 
                  specified in individual engagement agreements. In the absence of a specific agreement, disputes shall 
                  be governed by the laws of California, United States.
                </p>
                <p className="mt-4">
                  We encourage resolution of any concerns through direct communication. For formal disputes, we prefer 
                  confidential arbitration or mediation before pursuing litigation.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-2xl font-medium text-white">Contact for Legal Matters</h2>
                <p>
                  For questions regarding these disclaimers or legal compliance matters, please contact:
                </p>
                <div className="bg-white/5 p-6 rounded-lg border border-lime-400/20 mt-4">
                  <p><strong>Veridian Vanguard Advisors</strong></p>
                  <p>Legal and Compliance Department</p>
                  <p>Email: connect@veridianvanguard.com</p>
                  <p>Location: Los Angeles, CA</p>
                </div>
              </section>

              <div className="bg-lime-400/10 border border-lime-400/30 p-6 rounded-lg mt-8">
                <p className="text-white font-medium">
                  By using our website or engaging our advisory services, you acknowledge that you have read, understood, 
                  and agree to be bound by these disclaimers and all applicable terms and conditions.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-black py-12 mt-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center space-y-4">
              <div className="flex justify-center space-x-8 text-sm">
                <a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors underline">Privacy Policy</a>
                <a href="/terms-of-service" className="text-gray-400 hover:text-white transition-colors underline">Terms of Service</a>
                <a href="/disclaimers" className="text-lime-400 hover:text-lime-300 transition-colors underline">Disclaimers</a>
                <a href="https://readdy.ai/?ref=logo" className="text-gray-400 hover:text-lime-400 transition-colors underline">Powered by Readdy</a>
              </div>
              <div className="text-xs text-gray-500">
                © 2025 Veridian Vanguard Advisors. All Rights Reserved.
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Disclaimers;
